import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Plus, Printer, Download, ChevronDown, ChevronRight, MoreHorizontal, Eye, Pencil, Mail, Truck, RotateCcw, Trash2, X } from "lucide-react";
import AdminFulfillment from "@/components/AdminFulfillment";
import { totalVialsForLine, totalVialsForOrder, vialsPerUnit, formatLabelForItem, strengthForItem } from "@/lib/vialCount";

/**
 * AdminOrdersCommandCenter — Phase 1 Orders redesign.
 *
 * Operational cockpit. No dashboard cards, no charts. Universal search →
 * secondary filters → status views → clean table with inline item expansion.
 *
 * SCOPE LOCK: This component wraps the EXISTING order data + actions. It
 * changes ZERO backend, schema, payment, email, shipping, or fulfillment
 * logic. Every action (edit, view, print slip, tracking, note, refund,
 * delete, status change) is delegated to callbacks the parent already owns,
 * so we preserve every operational behavior verbatim.
 */

const STATUS_VIEWS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },              // derived: pending_payment
  { key: "paid", label: "Paid" },            // status=paid, not shipped/rts-ready
  { key: "processing", label: "Processing" },// derived: paid + local_delivery
  { key: "ready_to_ship", label: "Ready to Ship" }, // status=paid + !local + no label
  { key: "shipped", label: "Shipped" },
  { key: "delivered", label: "Delivered" },
  { key: "cancelled", label: "Cancelled" },
];

function isRtsCandidate(o) {
  return o.status === "paid" && !o.local_delivery && !o.shippo_transaction_id && !o.tracking_number;
}

function orderMatchesView(o, view) {
  switch (view) {
    case "all": return true;
    case "new": return o.status === "pending_payment";
    case "paid": return o.status === "paid" && !isRtsCandidate(o); // paid but already labeled or local
    case "processing": return o.status === "paid" && o.local_delivery === true;
    case "ready_to_ship": return isRtsCandidate(o);
    case "shipped": return o.status === "shipped";
    case "delivered": return o.status === "delivered";
    case "cancelled": return o.status === "cancelled";
    default: return true;
  }
}

/** Last Activity derivation — only from reliable stored data. Never fabricates timestamps. */
function computeLastActivity(o) {
  const events = [];
  const hist = Array.isArray(o.status_history) ? o.status_history : [];
  const isStripePending = o.status === "pending_payment" && (o.payment_method || "").toLowerCase() === "stripe";
  hist.forEach((h) => {
    const at = h?.at;
    if (!at) return;
    const map = {
      pending_payment: "Order placed",
      paid: "Payment received",
      shipped: "Shipped",
      delivered: "Delivered",
      cancelled: "Cancelled",
      abandoned: "Checkout abandoned",
    };
    let label = map[h.status];
    // Display-only override: while a Stripe order is still awaiting payment,
    // relabel its "Order placed" entry as "Checkout initiated". Does NOT
    // rewrite the stored status_history row; buyer-side history unchanged.
    if (isStripePending && h.status === "pending_payment") label = "Checkout initiated";
    if (label) events.push({ label, at, status: h.status });
  });
  const edits = Array.isArray(o.edit_history) ? o.edit_history : [];
  edits.forEach((e) => {
    const at = e?.at;
    if (!at) return;
    const fields = e?.fields_changed || [];
    let label = "Order edited";
    if (fields.includes("tracking_number")) label = "Tracking added";
    else if (fields.includes("address")) label = "Address edited";
    else if (fields.includes("status")) return; // covered by status_history
    events.push({ label, at, status: "_edit" });
  });
  if (!events.length) return { label: "—", at: null };
  // Sort newest first. Tie-breaker: when two events share a timestamp
  // (seeded or close-write), prefer the entry whose status matches the
  // order's current terminal status so e.g. an abandoned order never
  // displays "Order placed" as its last activity.
  events.sort((a, b) => {
    const d = new Date(b.at) - new Date(a.at);
    if (d !== 0) return d;
    if (a.status === o.status && b.status !== o.status) return -1;
    if (b.status === o.status && a.status !== o.status) return 1;
    return 0;
  });
  return events[0];
}

function fmtRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const dys = Math.floor(h / 24); if (dys < 30) return `${dys}d ago`;
  return d.toLocaleDateString();
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function paymentBadge(o) {
  const total = Number(o.total || 0);
  const refunded = Number(o.refunded_amount || 0);
  if (refunded > 0 && refunded >= total - 0.01) return { label: "Refunded", tone: "muted" };
  if (refunded > 0) return { label: "Partial refund", tone: "warn" };
  if (o.status === "cancelled") return { label: "Void", tone: "muted" };
  if (o.status === "abandoned") return { label: "Abandoned", tone: "muted" };
  if (o.status === "pending_payment") {
    const isStripe = (o.payment_method || "").toLowerCase() === "stripe";
    return { label: isStripe ? "Awaiting card" : "Pending", tone: "warn" };
  }
  return { label: "Paid", tone: "ok" };
}

function fulfillmentBadge(o) {
  if (o.status === "cancelled") return { label: "Cancelled", tone: "muted" };
  if (o.status === "abandoned") return { label: "Abandoned", tone: "muted" };
  if (o.status === "delivered") return { label: "Delivered", tone: "ok" };
  if (o.status === "shipped") return { label: "Shipped", tone: "ok" };
  if (o.status === "pending_payment") {
    const isStripe = (o.payment_method || "").toLowerCase() === "stripe";
    return { label: isStripe ? "Awaiting payment" : "New", tone: "warn" };
  }
  if (o.local_delivery) return { label: "Processing · Local", tone: "info" };
  if (isRtsCandidate(o)) return { label: "Ready to Ship", tone: "info" };
  return { label: "Processing", tone: "info" };
}

function shippingMethod(o) {
  if (o.local_delivery) {
    const mode = (o.local_delivery_mode || "delivery").toLowerCase();
    return mode === "pickup" ? "Local Pickup" : "Local Delivery";
  }
  const snap = o.shipping_snapshot;
  if (snap?.service_display) return snap.service_display;
  if (snap?.carrier) return snap.carrier;
  if (o.tracking_carrier) return o.tracking_carrier;
  return "—";
}

// Phase Purdy · Pass 1 — restrained apothecary status pill grammar.
// One canonical tone map used across Admin Orders + Order Workspace.
// - ok    → deep moss                (paid / shipped / delivered on list)
// - warn  → warm clay/mustard        (new / pending / partial refund)
// - info  → oxblood tint             (processing / ready to ship)
// - muted → stone/charcoal wash      (cancelled / void / refunded)
//           (cancelled is muted, never line-through)
function toneClasses(tone) {
  switch (tone) {
    case "ok":   return "bg-mossBg text-moss border-mossBorder";
    case "warn": return "bg-clayBg text-clay border-clayBorder";
    case "info": return "bg-oxblood/[0.06] text-oxblood border-oxblood/25";
    case "muted":default: return "bg-charcoal/5 text-charcoal/70 border-borderSubtle";
  }
}

function Badge({ label, tone }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-mono uppercase tracking-[0.14em] border ${toneClasses(tone)}`}>
      {label}
    </span>
  );
}

export default function AdminOrdersCommandCenter({
  orders,
  products = [],
  filteredOrdersFromParent, // parent's search filter (kept for parity with existing search prop)
  onOpenManualInvoice,
  onView, onEdit, onOpenNote, onOpenTracking, onOpenRefund, onDelete, onUpdateStatus,
}) {
  const [search, setSearch] = useState("");
  const [view, setView] = useState("all");
  const [dateFilter, setDateFilter] = useState("any"); // any|today|week|month
  const [paymentFilter, setPaymentFilter] = useState("any");
  const [shippingFilter, setShippingFilter] = useState("any");
  const [expanded, setExpanded] = useState(() => new Set());
  const [actionsFor, setActionsFor] = useState(null);

  const productsById = useMemo(() => {
    const m = {};
    for (const p of products) m[p.id] = p;
    return m;
  }, [products]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const now = Date.now();
    const dayMs = 86400000;
    return orders.filter((o) => {
      if (!orderMatchesView(o, view)) return false;
      if (s) {
        const hay = [
          o.order_number,
          o.customer_name,
          o.customer_email,
          o.tracking_number,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(s)) return false;
      }
      if (dateFilter !== "any") {
        const t = new Date(o.created_at).getTime();
        if (dateFilter === "today" && now - t > dayMs) return false;
        if (dateFilter === "week" && now - t > 7 * dayMs) return false;
        if (dateFilter === "month" && now - t > 30 * dayMs) return false;
      }
      if (paymentFilter !== "any" && o.payment_method !== paymentFilter) return false;
      if (shippingFilter !== "any") {
        const m = shippingMethod(o).toLowerCase();
        if (shippingFilter === "local" && !m.includes("local")) return false;
        if (shippingFilter === "carrier" && (m === "—" || m.includes("local"))) return false;
      }
      return true;
    });
  }, [orders, search, view, dateFilter, paymentFilter, shippingFilter]);

  const counts = useMemo(() => {
    const c = {};
    for (const v of STATUS_VIEWS) c[v.key] = orders.filter((o) => orderMatchesView(o, v.key)).length;
    return c;
  }, [orders]);

  const anyFilterActive = search || view !== "all" || dateFilter !== "any" || paymentFilter !== "any" || shippingFilter !== "any";
  const clearFilters = () => { setSearch(""); setView("all"); setDateFilter("any"); setPaymentFilter("any"); setShippingFilter("any"); };

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  return (
    <div data-testid="orders-command-center">
      {/* Universal search + primary tools */}
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[280px] max-w-lg">
          <Search size={14} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/60" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search order #, customer, email, or tracking #…"
            className="w-full h-11 pl-10 pr-4 rounded-full bg-white border border-borderSubtle text-sm placeholder:text-charcoal/50 focus:outline-none focus:border-charcoal transition-colors"
            data-testid="occ-search"
          />
        </div>
        <button
          onClick={onOpenManualInvoice}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-oxblood text-bone hover:bg-charcoal tracking-[0.2em] text-[13px] uppercase transition-colors rounded-full"
          data-testid="occ-new-manual-order"
        >
          <Plus size={12} strokeWidth={2}/> New manual order
        </button>
        <a
          href={`${BACKEND}/api/admin/orders/bulk-slips.pdf?status_filter=paid`}
          className="inline-flex items-center gap-2 px-4 py-2.5 border border-borderSubtle hover:border-charcoal text-charcoal tracking-[0.2em] text-[13px] uppercase transition-colors rounded-full"
          target="_blank" rel="noreferrer"
          data-testid="occ-bulk-slips"
        >
          <Printer size={12} strokeWidth={2}/> Print all slips
        </a>
        <a
          href={`${BACKEND}/api/admin/orders-export.csv`}
          className="inline-flex items-center gap-2 px-4 py-2.5 border border-borderSubtle hover:border-charcoal text-charcoal tracking-[0.2em] text-[13px] uppercase transition-colors rounded-full"
          data-testid="occ-csv"
        >
          <Download size={12} strokeWidth={2}/> Download CSV
        </a>
      </div>

      {/* Secondary filters */}
      <div className="mb-4 flex items-center gap-2 flex-wrap text-[13px]">
        <FilterSelect label="Date" value={dateFilter} onChange={setDateFilter} options={[
          { v: "any", l: "Any date" }, { v: "today", l: "Today" }, { v: "week", l: "Past 7 days" }, { v: "month", l: "Past 30 days" },
        ]} testid="occ-filter-date"/>
        <FilterSelect label="Payment" value={paymentFilter} onChange={setPaymentFilter} options={[
          { v: "any", l: "Any method" }, { v: "stripe", l: "Stripe" }, { v: "venmo", l: "Venmo" }, { v: "cashapp", l: "Cash App" }, { v: "manual", l: "Manual" },
        ]} testid="occ-filter-payment"/>
        <FilterSelect label="Shipping" value={shippingFilter} onChange={setShippingFilter} options={[
          { v: "any", l: "Any method" }, { v: "carrier", l: "Carrier ship" }, { v: "local", l: "Local (delivery/pickup)" },
        ]} testid="occ-filter-shipping"/>
        {anyFilterActive && (
          <button onClick={clearFilters} className="inline-flex items-center gap-1 px-3 py-1.5 text-charcoal/75 hover:text-oxblood text-[11px] uppercase tracking-[0.2em] font-mono" data-testid="occ-clear-filters">
            <X size={12}/> Clear filters
          </button>
        )}
      </div>

      {/* Status views */}
      <div className="mb-6 flex items-center gap-1 border-b border-borderSubtle overflow-x-auto" data-testid="occ-status-tabs">
        {STATUS_VIEWS.map((v) => {
          const active = view === v.key;
          return (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              className={`whitespace-nowrap px-4 py-2.5 border-b-2 -mb-px text-[13px] uppercase tracking-[0.2em] transition-colors ${active ? "border-oxblood text-oxblood" : "border-transparent text-charcoal/60 hover:text-charcoal"}`}
              data-testid={`occ-view-${v.key}`}
            >
              {v.label} <span className="ml-1 font-mono text-[11px] tabular-nums opacity-75">{counts[v.key]}</span>
            </button>
          );
        })}
      </div>

      {/* Content — Ready to Ship view delegates to the existing fulfillment queue */}
      {view === "ready_to_ship" ? (
        <div className="rounded-lg border border-borderSubtle bg-white" data-testid="occ-rts-embedded">
          <AdminFulfillment />
        </div>
      ) : (
        <div className="border border-borderSubtle bg-white overflow-x-auto" data-testid="occ-orders-table">
          <table className="w-full text-sm">
            <thead className="bg-bone border-b border-borderSubtle">
              <tr className="text-left">
                <th className="overline px-3 py-3">Order #</th>
                <th className="overline px-3 py-3">Customer</th>
                <th className="overline px-3 py-3">Items</th>
                <th className="overline px-3 py-3">Date</th>
                <th className="overline px-3 py-3">Total</th>
                <th className="overline px-3 py-3">Payment</th>
                <th className="overline px-3 py-3">Fulfillment</th>
                <th className="overline px-3 py-3">Shipping</th>
                <th className="overline px-3 py-3">Last activity</th>
                <th className="overline px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-charcoal/60">
                  {anyFilterActive ? "No orders match your filters." : "No orders yet."}
                </td></tr>
              )}
              {filtered.map((o) => {
                const isOpen = expanded.has(o.id);
                const pm = paymentBadge(o);
                const fb = fulfillmentBadge(o);
                const la = computeLastActivity(o);
                const totalVials = totalVialsForOrder(o, productsById);
                const lineCount = (o.items || []).length;
                return (
                  <FragmentRow
                    key={o.id}
                    o={o}
                    isOpen={isOpen}
                    onToggle={() => toggleExpanded(o.id)}
                    pm={pm} fb={fb} la={la}
                    totalVials={totalVials} lineCount={lineCount}
                    productsById={productsById}
                    onUpdateStatus={onUpdateStatus}
                    actionsFor={actionsFor} setActionsFor={setActionsFor}
                    onView={onView} onEdit={onEdit} onOpenNote={onOpenNote}
                    onOpenTracking={onOpenTracking} onOpenRefund={onOpenRefund} onDelete={onDelete}
                    BACKEND={BACKEND}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-borderSubtle bg-white">
      <span className="text-[11px] uppercase tracking-[0.18em] text-charcoal/60 font-mono">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="bg-transparent text-[13px] focus:outline-none" data-testid={testid}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

function FragmentRow({ o, isOpen, onToggle, pm, fb, la, totalVials, lineCount, productsById, onUpdateStatus, actionsFor, setActionsFor, onView, onEdit, onOpenNote, onOpenTracking, onOpenRefund, onDelete, BACKEND }) {
  const showRefund = o.payment_method === "stripe" && o.status === "paid";
  return (
    <>
      <tr className="border-b border-borderSubtle hover:bg-bone/40 transition-colors" data-testid={`occ-row-${o.order_number}`}>
        <td className="px-3 py-3 font-mono">
          {/* iter185 Phase 2 — Order # is now a link into the Individual Order Workspace.
              iter190c — bumped to 13px medium for scannability of the primary identifier. */}
          <Link
            to={`/admin/orders/${o.id}`}
            className="text-charcoal hover:text-oxblood transition-colors text-[13px] font-medium"
            data-testid={`occ-order-link-${o.order_number}`}
          >
            #{o.order_number}
          </Link>
        </td>
        <td className="px-3 py-3">
          {/* iter190c — Customer name is a primary identifier; bumped to 14px medium
              so it visibly dominates the email row beneath it. Email stays at 12px muted. */}
          <p className="text-[14px] font-medium leading-tight flex items-center gap-2">
            {o.customer_name}
            {o.customer_deleted && <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-charcoal/10 text-charcoal/75 border border-borderSubtle" title="Customer account deleted — order preserved">deleted</span>}
          </p>
          <p className="text-[12px] text-charcoal/60 leading-tight">{o.customer_email}</p>
        </td>
        {/* iter184 Phase 1 — dedicated ITEMS column. Line/vial summary + expand
            control moved out from underneath the customer info. Compact by design. */}
        <td className="px-3 py-3 align-top">
          <p className="text-[13px] text-charcoal/90 leading-tight">{lineCount} line item{lineCount !== 1 ? "s" : ""}</p>
          <p className="text-[13px] text-charcoal/70 leading-tight">{totalVials == null ? "—" : `${totalVials} total vial${totalVials !== 1 ? "s" : ""}`}</p>
          <button
            onClick={onToggle}
            className="mt-1 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] font-mono text-oxblood hover:text-charcoal transition-colors"
            data-testid={`occ-view-items-${o.order_number}`}
            title={isOpen ? "Collapse items" : "View items"}
          >
            {isOpen ? <ChevronDown size={12}/> : <ChevronRight size={12}/>}
            {isOpen ? "Hide items" : "View items"}
          </button>
        </td>
        <td className="px-3 py-3 text-[13px] text-charcoal/80 whitespace-nowrap">{fmtDateTime(o.created_at)}</td>
        <td className="px-3 py-3 font-mono text-oxblood whitespace-nowrap">${Number(o.total).toFixed(2)}</td>
        <td className="px-3 py-3"><Badge label={pm.label} tone={pm.tone}/><p className="text-[11px] text-charcoal/60 uppercase tracking-widest font-mono mt-1">{o.payment_method}</p></td>
        <td className="px-3 py-3">
          {/* iter184 Phase 1 — status BADGE only; inline dropdown removed to reduce
              visual noise. Status change lives in the ⋯ Actions menu as
              "Change status". Existing handler (`onUpdateStatus`) is untouched. */}
          <Badge label={fb.label} tone={fb.tone}/>
        </td>
        <td className="px-3 py-3 text-[13px] text-charcoal/80">{shippingMethod(o)}</td>
        <td className="px-3 py-3 text-[13px]">
          <p className="text-charcoal">{la.label}</p>
          <p className="text-[11px] text-charcoal/60 whitespace-nowrap" title={fmtDateTime(la.at)}>{la.at ? fmtRelative(la.at) : ""}</p>
        </td>
        <td className="px-3 py-3 text-right relative">
          <button
            onClick={() => setActionsFor(actionsFor === o.id ? null : o.id)}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-borderSubtle hover:border-oxblood hover:text-oxblood text-charcoal/75 transition-colors"
            data-testid={`occ-actions-${o.order_number}`}
          >
            <MoreHorizontal size={14}/>
          </button>
          {actionsFor === o.id && (
            <div className="absolute right-2 top-full mt-1 z-20 w-56 rounded-md border border-borderSubtle bg-white shadow-lg py-1 text-left" data-testid={`occ-actions-menu-${o.order_number}`}>
              {/* iter185 Phase 2 — View full order now navigates to the workspace. */}
              <ActionLink icon={<Eye size={13}/>} label="View full order" href={`/admin/orders/${o.id}`} internal testid={`occ-view-full-${o.order_number}`}/>
              <ActionRow icon={<Pencil size={13}/>} label="Edit order" onClick={() => { onEdit(o); setActionsFor(null); }}/>
              <ActionRow icon={<Mail size={13}/>}  label={o.note_to_buyer ? "Edit note to buyer" : "Add note to buyer"} onClick={() => { onOpenNote(o); setActionsFor(null); }}/>
              <ActionRow icon={<Truck size={13}/>} label={o.tracking_number ? "Update tracking" : "Add tracking"} onClick={() => { onOpenTracking(o); setActionsFor(null); }}/>
              <ActionLink icon={<Printer size={13}/>} label="Print packing slip" href={`${BACKEND}/api/orders/${o.id}/slip.pdf`}/>
              {showRefund && <ActionRow icon={<RotateCcw size={13}/>} label="Refund (Stripe)" onClick={() => { onOpenRefund(o); setActionsFor(null); }}/>}
              {/* Change Status — expanded inline; picks a value then closes menu. */}
              <div className="border-t border-borderSubtle my-1"/>
              <div className="px-3 py-2">
                <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-charcoal/60 mb-1">Change status</p>
                <select
                  value={o.status}
                  onChange={(e) => { onUpdateStatus(o.id, e.target.value); setActionsFor(null); }}
                  className="w-full h-8 text-[12px] font-mono uppercase tracking-widest border border-borderSubtle rounded-sm bg-white px-2"
                  data-testid={`occ-status-${o.order_number}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <option value="pending_payment">Pending payment</option>
                  <option value="paid">Paid</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="abandoned">Abandoned</option>
                </select>
              </div>
              <div className="border-t border-borderSubtle my-1"/>
              <ActionRow icon={<Trash2 size={13}/>} label="Delete order" tone="danger" onClick={() => { setActionsFor(null); onDelete(o.id, o.order_number); }}/>
            </div>
          )}
        </td>
      </tr>
      {isOpen && (
        <tr className="bg-bone/30" data-testid={`occ-items-${o.order_number}`}>
          <td colSpan={10} className="px-6 py-5">
            <div className="border border-borderSubtle rounded-lg overflow-hidden bg-white">
              <table className="w-full text-sm">
                <thead className="bg-bone">
                  <tr className="text-left">
                    <th className="overline px-4 py-2">Product</th>
                    <th className="overline px-4 py-2">Strength</th>
                    <th className="overline px-4 py-2">Format</th>
                    <th className="overline px-4 py-2 text-right">Quantity</th>
                    <th className="overline px-4 py-2 text-right">Total vials</th>
                    <th className="overline px-4 py-2 text-right">Line total</th>
                  </tr>
                </thead>
                <tbody>
                  {(o.items || []).map((it, idx) => {
                    const per = vialsPerUnit(it, productsById);
                    const total = totalVialsForLine(it, productsById);
                    return (
                      <tr key={idx} className="border-t border-borderSubtle">
                        <td className="px-4 py-3 font-medium">{it.name || "—"}
                          {it.price_override != null && Number(it.price_override) === 0 && <span className="ml-2 text-[10px] uppercase tracking-[0.18em] font-mono text-oxblood">Comp</span>}
                        </td>
                        <td className="px-4 py-3 text-charcoal/80">{strengthForItem(it, productsById)}</td>
                        <td className="px-4 py-3 text-charcoal/80">{formatLabelForItem(it, productsById)}</td>
                        <td className="px-4 py-3 font-mono text-right">{it.quantity}</td>
                        <td className="px-4 py-3 font-mono text-right">{total == null ? "—" : total}</td>
                        <td className="px-4 py-3 font-mono text-right text-oxblood">${(Number(it.price) * Number(it.quantity)).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-bone border-t border-borderSubtle">
                  <tr>
                    <td colSpan={6} className="px-4 py-3 text-right text-[13px] text-charcoal/75">
                      <span className="mr-4">{o.items.length} line item{o.items.length !== 1 ? "s" : ""}</span>
                      <span className="font-medium text-charcoal">{totalVialsForOrder(o, productsById) == null ? "—" : `${totalVialsForOrder(o, productsById)} total vials`}</span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function ActionRow({ icon, label, onClick, tone }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 text-[13px] text-left hover:bg-bone transition-colors ${tone === "danger" ? "text-charcoal hover:text-oxblood" : "text-charcoal"}`}
    >
      <span className="text-charcoal/60">{icon}</span>{label}
    </button>
  );
}
function ActionLink({ icon, label, href, internal, testid }) {
  // iter185 Phase 2 — support in-app navigation for the workspace link.
  if (internal) {
    return (
      <Link to={href} className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-charcoal hover:bg-bone transition-colors" data-testid={testid}>
        <span className="text-charcoal/60">{icon}</span>{label}
      </Link>
    );
  }
  return (
    <a href={href} target="_blank" rel="noreferrer" className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-charcoal hover:bg-bone transition-colors" data-testid={testid}>
      <span className="text-charcoal/60">{icon}</span>{label}
    </a>
  );
}
